import React, { useEffect, useRef, useState } from 'react';
import { View, Pressable, Modal as ModalBase, Animated, Platform } from 'react-native';
import styled from 'styled-components/native';

import usePrefersReducedMotion from 'app/src/hooks/usePrefersReducedMotion';
import useScreenSize from 'app/src/hooks/useScreenSize';
import { Text } from 'app/src/styles';
import colors from 'app/src/styles/colors';
import { duration } from 'app/src/styles/motion';

const CenteredView = styled(Animated.View)`
  flex: 1;
  background-color: ${colors.overlay};
  ${props => props.isMobile ? '' : `
    justify-content: center;
    align-items: center;
  `}
`;

const ModalView = styled(Animated.View)`
  ${props => props.isMobile ? `
    max-width: 100vw;
    width: 100%;
  ` : `
    width: fit-content;
    min-width: 50%;
    border-radius: 8px;
    overflow: hidden;
  `}
  height: fit-content;
  min-height: fit-content;
  background-color: ${colors.background};
`;

const Header = styled(View)`
  background-color: ${colors.header};
  padding: 16px 20px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled(Text)`
  color: ${colors.textOnDark};
  font-weight: 600;
  font-size: 18px;
`;

const Close = styled(Text)`
  color: ${colors.textOnDark};
  font-weight: 600;
  font-size: 18px;
  margin-left: 24px;
  opacity: 0.8;
`;

const Content = styled(View)`
  ${props => props.isMobile ? `
    width: 100vw;
    max-height: 90vh;
  ` : `
    max-height: 80vh;
    max-width: 90vw;
  `}
  padding: 24px;
  overflow: auto;
`;

const Modal = ({ title, children, setShow }) => {
  const { isScreenSize } = useScreenSize();
  const isMobile = isScreenSize('mobile');
  const prefersReducedMotion = usePrefersReducedMotion();

  const overlayOpacity = useRef(new Animated.Value(0)).current;
  const contentOpacity = useRef(new Animated.Value(0)).current;
  const contentTranslateY = useRef(new Animated.Value(20)).current;
  const [isVisible, setIsVisible] = useState(true);

  const animationDuration = prefersReducedMotion ? 0 : duration.normal;

  // Animate in on mount
  useEffect(() => {
    Animated.parallel([
      Animated.timing(overlayOpacity, {
        toValue: 1,
        duration: animationDuration,
        useNativeDriver: true,
      }),
      Animated.timing(contentOpacity, {
        toValue: 1,
        duration: animationDuration,
        useNativeDriver: true,
      }),
      Animated.timing(contentTranslateY, {
        toValue: 0,
        duration: animationDuration,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  // Handle close with exit animation
  const handleClose = () => {
    if (prefersReducedMotion) {
      setShow(false);
      return;
    }

    Animated.parallel([
      Animated.timing(overlayOpacity, {
        toValue: 0,
        duration: animationDuration,
        useNativeDriver: true,
      }),
      Animated.timing(contentOpacity, {
        toValue: 0,
        duration: animationDuration,
        useNativeDriver: true,
      }),
      Animated.timing(contentTranslateY, {
        toValue: 20,
        duration: animationDuration,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setShow(false);
    });
  };

  return (
    <ModalBase
      onRequestClose={handleClose}
      visible={isVisible}
      transparent
    >
      <CenteredView
        isMobile={isMobile}
        style={{ opacity: overlayOpacity }}
      >
        <ModalView
          isMobile={isMobile}
          style={{
            opacity: contentOpacity,
            transform: [{ translateY: contentTranslateY }],
          }}
        >
          <Header>
            <Title>{title}</Title>
            <Pressable onPress={handleClose}>
              <Close>X</Close>
            </Pressable>
          </Header>
          <Content isMobile={isMobile}>
            {children}
          </Content>
        </ModalView>
      </CenteredView>
    </ModalBase>
  );
};

export default Modal;
